/*
 * product-ijewel-prefetch.js — quiet GLB cache warmer for the iJewel viewer.
 *
 * Goal: by the time the shopper picks a variant that needs a different model,
 * its GLB is already in the browser HTTP cache, so the bridge's
 * `viewer.load(url)` costs parse time only (~100–200ms) instead of a network
 * round-trip. Shopify's model CDN sends `cache-control: max-age=31557600` and
 * `access-control-allow-origin: *`, so a plain CORS fetch warms the same cache
 * entry the viewer reads.
 *
 * The variant count is irrelevant: entries in `window.iJewelVariantMap`
 * ({ "<variantId>": { url, cad } }) collapse to a handful of DISTINCT cads —
 * geometry changes only with shape/size, never metal — and sibling metal
 * products share the same cad_codes, hence the same URLs. Warming the deduped
 * set of the current product therefore covers the whole combined listing.
 *
 * Strategy — only the selected variant's GLB loads on the critical path.
 * No sibling network warm before `ijewel:model-ready` (avoids contending with
 * SDK env packs + first-load settle). After model-ready:
 *   1. Arm + background warm: nearest shapes first, one file at a time,
 *      idle-yielded (mobile WARM_LIMIT is tighter).
 *   2. Intent (purchase-form controls only — not the viewer root): hover /
 *      focus / touch warms the hovered cad when it differs, else distance-1.
 *   3. Follow: each `variant:update` re-ranks and warms distance-1.
 *
 * Listens only — no changes to the variant engine or the bridge:
 *   - `ijewel:model-ready` (document)     → unlock warm + start background queue
 *   - `ijewel:cad-resolved` (document)   → a CAD's GLB url resolved; requeue
 *   - `ijewel:refresh-context` (document)→ metal switch swapped the map; requeue
 *   - `variant:update` (document)        → re-rank + warm (only after model-ready)
 *
 * Respects the shopper's data: disabled entirely under Save-Data or 2g.
 */
// @ts-nocheck — vanilla JS relying on custom window.* globals, as the bridge.
(function () {
  'use strict';

  // Mobile / touch devices — notably iOS Safari, where navigator.connection is
  // unavailable so connectionBlocksPrefetch() can't throttle us — have a tight
  // GPU/JS memory ceiling. Warm far fewer GLBs upfront so speculative caching
  // can't contribute to a webglcontextlost freeze. Intent-driven warming (a
  // shopper actually touching an option) is unaffected. Desktop keeps the full
  // look-ahead.
  var IS_MOBILE =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 767.98px), (pointer: coarse)').matches;

  var WARM_LIMIT = IS_MOBILE ? 2 : 8; // rings have 4 distinct GLBs, bracelets 16

  var warmed = {}; // url -> true (fetched or in flight)
  var queue = []; // urls, nearest-first
  var draining = false;
  var modelReady = false; // no sibling network warm until ijewel:model-ready
  var armed = false; // background queue active (implies modelReady)
  var currentVariant = null;
  var optionCount = 0;
  var optionNames = [];
  var productVariants = [];

  if (connectionBlocksPrefetch()) return;

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    var root = document.querySelector('[data-ijewel-root]');
    if (!root) return;
    /* Product-group opt-out: the viewer markup is still rendered (CSS hides it
       below 768px and product-model-ijewel.js refuses to boot), but this file
       would otherwise still resolve and warm GLBs on phones — defeating the
       whole point of the flag. See product-main-gallery.liquid. */
    if (IS_MOBILE && root.closest('[data-ijewel-desktop-only]')) return;

    readContext();
    currentVariant = resolveInitialVariant();
    // The viewer fetches the initial model itself — never re-fetch it.
    markCurrentUrlWarmed();

    document.addEventListener('ijewel:model-ready', function () {
      modelReady = true;
      // Desktop: arm + background warm immediately after first-load settle.
      // Mobile: stay INERT until real intent (touch on the purchase form or a
      // variant change) — auto-warming 2 GLBs (~3.5MB) on every PDP view was
      // a measurable contributor to iOS Safari's per-tab memory/bandwidth
      // budget. onIntent / variant:update below arm the queue on mobile.
      if (!IS_MOBILE) {
        armed = true;
        requeue();
      }
    });
    /* Deliberately NO requeue on `ijewel:cad-resolved`. The queue is built from
       Layer A, which is complete at page load, so a single requeue per selection
       already yields the full WARM_LIMIT set — nothing later "arrives" that
       changes the ranking. Re-queueing per resolution instead cascades: each
       warmed CAD is excluded from the next ranking, so requeue hands back 8 FRESH
       candidates and the loop walks the entire product (measured: 50/50 CADs
       resolved on Georgian Pinky Ring, ~34 background requests and climbing).
       The old `ijewel:map-loaded` signal was safe to requeue on because it fired
       once; this one fires per CAD. Selection changes and metal switches requeue
       via `variant:update` / `ijewel:refresh-context` below. */
    document.addEventListener('ijewel:refresh-context', function () {
      readContext();
      requeue();
    });

    document.addEventListener('variant:update', function (event) {
      var variant = event && event.detail && event.detail.resource;
      if (!variant) return;
      currentVariant = variant;
      // Keep selection current during boot, but never arm sibling warm early.
      if (!modelReady) return;
      /* `variant:update` is NOT proof of intent. It is dispatched by
         product-purchase-form.js, product-compound-options.js and
         sticky-add-to-cart.js, including on paths that run without the shopper
         touching anything — so arming here defeated the mobile inert policy
         documented on `ijewel:model-ready` above. Measured on an iPhone
         viewport with zero interaction: two speculative GLBs (794KB + 969KB =
         1.76MB) fetched at 18.4s and 23.7s, on top of the 862KB model actually
         being displayed. Mobile now arms only from onIntent (touch / focus /
         pointerover inside the purchase form); once genuinely armed, later
         variant changes requeue exactly as before. Desktop is unchanged. */
      if (IS_MOBILE && !armed) return;
      armed = true;
      // Requeue nearest-first: models one option-change from the new
      // selection warm first, sequentially — speculative, so stay gentle.
      requeue();
    });

    // Intent: purchase-form option controls only (not the viewer — touch/scroll
    // on [data-ijewel-root] used to arm warm during first-load settle).
    // pointerenter doesn't bubble; pointerover does.
    document.addEventListener('pointerover', onIntent, { passive: true });
    document.addEventListener('focusin', onIntent);
    document.addEventListener('touchstart', onIntent, { passive: true });
  }

  function connectionBlocksPrefetch() {
    var conn = navigator.connection;
    if (!conn) return false;
    if (conn.saveData) return true;
    return /(^|-)2g$/.test(conn.effectiveType || '');
  }

  /* ---- context (same sources as the bridge) ------------------------------
     Duplicated from product-ijewel-bridge.js on purpose: the prefetcher must
     be standalone and load-order-independent, so it doesn't share globals
     with the bridge. Keep the copies in sync if the resolution logic changes. */

  function readContext() {
    var node = document.querySelector('.product-purchase-form__product-json');
    var product = null;
    try {
      product = node && JSON.parse(node.textContent);
    } catch (_e) {
      /* keep previous context */
    }
    if (!product) return;
    productVariants = product.variants || [];
    optionCount = (product.options || []).length;
    optionNames = (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : (o && o.name) || '';
    });
  }

  function resolveInitialVariant() {
    var input = document.querySelector(
      '.product-purchase-form [data-product-form-variant-id], .product-purchase-form [name="id"]'
    );
    var id = input && input.value;
    if (!id) return productVariants[0] || null;
    return (
      productVariants.find(function (v) {
        return String(v.id) === String(id);
      }) || productVariants[0] || null
    );
  }

  function variantOptionValue(variant, position) {
    if (window.ProductVariantMatrix && window.ProductVariantMatrix.optionValue) {
      return window.ProductVariantMatrix.optionValue(variant, position);
    }
    return variant && variant['option' + position];
  }

  function markCurrentUrlWarmed() {
    var entry = currentVariant && (window.iJewelVariantMap || {})[String(currentVariant.id)];
    if (entry && entry.url) warmed[entry.url] = true;
  }

  function readFormOptionValue(position) {
    var nodes = document.querySelectorAll(
      '.product-purchase-form [data-option-select][data-option-position="' + position + '"]'
    );
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.tagName === 'CUSTOM-SELECT-COMPONENT') {
        var hidden = el.querySelector('input[type="hidden"]');
        if (hidden && hidden.value) return hidden.value;
      } else if (el.tagName === 'SELECT' && el.value) {
        return el.value;
      } else if (el.type === 'radio') {
        if (el.checked && el.value) return el.value;
      } else if (el.type === 'hidden' && el.value) {
        return el.value;
      }
    }
    return null;
  }

  function readSelectedOptions() {
    var out = [];
    for (var p = 1; p <= optionCount; p++) {
      out.push(readFormOptionValue(p));
    }
    return out;
  }

  function findVariantForOptions(selectedOptions) {
    if (!selectedOptions.length) return null;
    if (!selectedOptions.every(function (v) {
      return v != null && v !== '';
    })) {
      return null;
    }

    function matchesSelection(variant, skipEmptyVariantOptions) {
      return selectedOptions.every(function (val, i) {
        if (val == null || val === '') return true;
        var variantVal = variantOptionValue(variant, i + 1);
        if (variantVal == null && skipEmptyVariantOptions) return true;
        return variantVal === val;
      });
    }

    var exact = productVariants.find(function (v) {
      return matchesSelection(v, false);
    });
    if (exact) return exact;

    var candidates = productVariants.filter(function (v) {
      return matchesSelection(v, true);
    });
    if (!candidates.length) return null;
    if (candidates.length === 1) return candidates[0];

    var compoundIdx = optionNames.findIndex(function (name) {
      return name && String(name).indexOf(':') !== -1;
    });
    if (compoundIdx >= 0 && selectedOptions[compoundIdx]) {
      var compoundMatches = candidates.filter(function (v) {
        return variantOptionValue(v, compoundIdx + 1) === selectedOptions[compoundIdx];
      });
      if (compoundMatches.length) {
        return compoundMatches.find(function (v) {
          return v.available;
        }) || compoundMatches[0];
      }
    }

    return (
      candidates.find(function (v) {
        return v.available;
      }) || candidates[0]
    );
  }

  /** cad for a variant id: resolved map first, then Layer A. */
  function cadForVariantId(id) {
    if (!id) return '';
    var resolver = window.IjewelCadResolver;
    if (resolver) return resolver.cadForVariant(id);
    var entry = (window.iJewelVariantMap || {})[String(id)];
    if (entry && entry.cad) return entry.cad;
    return (window.iJewelVariantCadMap || {})[String(id)] || '';
  }

  function currentCad() {
    return currentVariant ? cadForVariantId(currentVariant.id) : '';
  }

  function intentInputFromTarget(target) {
    if (!target || !target.closest) return null;
    if (target.matches && target.matches('.product-purchase-form [data-option-select]')) return target;

    var label = target.closest(
      '.product-option-accordion__swatch, .product-option-accordion__row, .product-purchase-form label[for]'
    );
    if (label) {
      var forId = label.getAttribute('for');
      var byFor = forId && document.getElementById(forId);
      if (byFor && byFor.matches('[data-option-select]')) return byFor;
      var sibling = label.previousElementSibling;
      if (sibling && sibling.matches('[data-option-select]')) return sibling;
    }

    var control = target.closest('.product-purchase-form [data-option-select]');
    return control || null;
  }

  /** Hover/focus on a specific option value → the CAD it needs, if it differs. */
  function cadForHoveredOption(target) {
    var input = intentInputFromTarget(target);
    if (!input || input.disabled || input.checked) return null;

    var pos = parseInt(input.getAttribute('data-option-position') || '', 10);
    if (!pos) return null;

    var selected = readSelectedOptions();
    selected[pos - 1] = input.value;

    var variant = findVariantForOptions(selected);
    if (!variant) return null;

    // Layer A, so a shape whose GLB has never been resolved is still a valid
    // hover candidate — previously it was skipped for having no url yet.
    var cad = cadForVariantId(variant.id);
    if (!cad) return null;

    var current = currentCad();
    if (current && cad === current) return null;

    return { cad: cad, variantId: String(variant.id) };
  }

  /* ---- ranking ------------------------------------------------------------
   * Dedupe the map by cad, scoring each cad with the smallest number of option
   * changes needed to reach it from the current selection. `maxDistance`
   * limits the result (1 = reachable by touching a single option). */

  function optionDistance(variant) {
    if (!currentVariant) return 1;
    var d = 0;
    for (var p = 1; p <= optionCount; p++) {
      if (variantOptionValue(variant, p) !== variantOptionValue(currentVariant, p)) d++;
    }
    return d;
  }

  // changedPosition: only count variants whose sole difference is at this position.
  /* Rank by CAD, from Layer A (`window.iJewelVariantCadMap`) rather than the
     resolved GLB map. The resolved map now only holds CADs we have already
     fetched, so ranking off it could never discover a NEW shape to warm — the
     scheduler has to see all candidates and then ask the resolver for the ones
     it picks. Returns descriptors, not urls, because the url may not exist yet. */
  function rankedCads(maxDistance, changedPosition) {
    var cadMap = window.iJewelVariantCadMap || {};
    var resolved = window.iJewelVariantMap || {};
    var variantsById = {};
    productVariants.forEach(function (v) {
      variantsById[String(v.id)] = v;
    });

    var ids = Object.keys(cadMap);
    // Layer A absent (older cached HTML) → fall back to whatever is resolved so
    // this degrades to the old behaviour instead of going inert.
    if (!ids.length) ids = Object.keys(resolved);

    var current = currentCad();
    var byCad = {}; // cad -> {cad, variantId, dist}
    ids.forEach(function (id) {
      var cad = cadMap[id] || (resolved[id] && resolved[id].cad);
      if (!cad || cad === current) return;
      // Already on the network → nothing left to warm for this geometry.
      var knownUrl = resolved[id] && resolved[id].url;
      if (knownUrl && warmed[knownUrl]) return;

      var variant = variantsById[id];
      var dist = variant ? optionDistance(variant) : optionCount || 1;
      if (changedPosition && variant) {
        if (
          dist !== 1 ||
          variantOptionValue(variant, changedPosition) ===
            variantOptionValue(currentVariant, changedPosition)
        ) {
          return;
        }
      }
      var prev = byCad[cad];
      if (!prev || dist < prev.dist) byCad[cad] = { cad: cad, variantId: id, dist: dist };
    });

    return Object.keys(byCad)
      .map(function (cad) {
        return byCad[cad];
      })
      .filter(function (item) {
        return !maxDistance || item.dist <= maxDistance;
      })
      .sort(function (a, b) {
        return a.dist - b.dist;
      });
  }

  /* Resolve a ranked CAD to its GLB url (cache hit → no request) and warm it.
     One request per CAD ever, shared with the bridge's own resolution, so a
     shape the shopper then selects is already both resolved AND warmed. */
  function warmCad(item, options) {
    if (!item || !item.cad || !modelReady) return Promise.resolve();
    var resolver = window.IjewelCadResolver;
    if (!resolver) return Promise.resolve();
    var known = resolver.peek(item.cad);
    if (known) return warm(known.url, options);
    return resolver
      .resolve(item.cad, item.variantId)
      .then(function (result) {
        return warm(result.url, options);
      })
      .catch(function () {
        /* resolver already logged variant/cad/endpoint/reason */
      });
  }

  /* ---- intent -------------------------------------------------------------
   * Any option control inside the purchase form counts; if it carries a
   * position (native pickers: data-option-position, compound accordions:
   * data-compound-position) we warm just that axis, else everything near. */

  function onIntent(event) {
    if (!modelReady) return;
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('.product-purchase-form')) return;

    armed = true;

    var hovered = cadForHoveredOption(target);
    if (hovered) {
      // Hover priority: jump the queue and resolve+warm this one CAD now.
      warmCad(hovered, { priority: 'high' });
      return;
    }

    var control = target.closest(
      '.product-purchase-form [data-option-select], .product-purchase-form [data-option-position], [data-compound-accordion]'
    );
    if (!control) return;

    var pos = parseInt(
      control.getAttribute('data-option-position') ||
        control.getAttribute('data-compound-position') ||
        '',
      10
    );
    // Capped at WARM_LIMIT: each of these may now cost a targeted resolve, so
    // an axis with 10 shapes must not fan out into 10 concurrent requests.
    // requeue() picks up the remainder sequentially.
    var items = (pos ? rankedCads(1, pos) : rankedCads(1)).slice(0, WARM_LIMIT);
    items.forEach(function (item) {
      warmCad(item);
    });
    requeue();
  }

  /* ---- background queue ----------------------------------------------------
   * Inert until armed by a first interaction; then warms nearest-first, one
   * file at a time, yielding to idle time between files. */

  function requeue() {
    if (!armed) return;
    markCurrentUrlWarmed();
    // WARM_LIMIT is unchanged (desktop 8 / mobile 2), so the number of CADs the
    // scheduler touches — and therefore the number of targeted resolves — stays
    // exactly what it was before this change.
    queue = rankedCads().slice(0, WARM_LIMIT);
    drain();
  }

  function drain() {
    if (draining) return;
    draining = true;
    (function next() {
      var item = queue.shift();
      if (!item) {
        draining = false;
        return;
      }
      warmCad(item).then(yieldThenNext, yieldThenNext);
      function yieldThenNext() {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(next, { timeout: 2000 });
        } else {
          setTimeout(next, 250);
        }
      }
    })();
  }

  /* ---- the warm itself ----------------------------------------------------
   * Draining the body is what commits the response to the HTTP cache; the
   * viewer's own fetch for the same URL then reads it from disk/memory. */

  function warm(url, options) {
    if (!url) return Promise.resolve();
    // Selected GLB is marked in `warmed` at init; siblings stay off-network
    // until model-ready so we don't contend with packs env + settle.
    if (!modelReady) return Promise.resolve();
    // Mirror bridge normalizeAssetUrl — protocol-relative http:// on theme-dev
    // fails Safari CORS on the CDN's https redirect.
    if (url.indexOf('//') === 0) url = 'https:' + url;
    else if (url.indexOf('http://') === 0) url = 'https://' + url.slice(7);
    if (warmed[url]) return Promise.resolve();
    warmed[url] = true;
    var fetchPriority = options && options.priority === 'high' ? 'high' : 'low';
    return fetch(url, { mode: 'cors', credentials: 'omit', priority: fetchPriority })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.blob();
      })
      .then(function () {
        // Announce so the bridge can pre-parse the file into its model cache.
        document.dispatchEvent(
          new CustomEvent('ijewel:glb-warmed', { detail: { url: url } })
        );
      })
      .catch(function () {
        delete warmed[url]; // allow a later trigger to retry
      });
  }

  // Narrow public surface so other modules (product-group-selector.js, for a
  // sibling metal switch) can warm a specific URL through the same cache/dedup
  // without duplicating the fetch+blob+announce logic. Absent under Save-Data
  // /2g, same as the rest of this module — callers must optional-chain.
  window.iJewelPrefetch = { warm: warm, mobile: IS_MOBILE, warmLimit: WARM_LIMIT };
})();
